import React from 'react';

/**
 * Lumio panel glyphs — exact vectors from the Figma Lumio node (4662:343),
 * inlined so they tint via `currentColor` (Solar icon set: Fire, Square Academic
 * Cap 2, Arrow Up). Plus a few state glyphs with no design yet (NewChat, Stop).
 *
 * Interim home: these belong in the shared `components/Icon` set, but that
 * component is under a teammate's active claim — move them there (extend
 * `IconName`) once the claim frees. The multicolour Lumio logo mark is NOT a
 * tintable glyph and renders as a plain `<img>` asset (its gradient stays intact).
 */
type GlyphProps = { size?: number; className?: string };

/** Bold / Essentional, UI / Menu Dots (node 4662:350): the "..." past-conversations trigger. */
export const MenuDotsIcon = ({ size = 16, className }: GlyphProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <g transform="translate(2 6.66667)" fill="currentColor">
      <path d="M2.66667 1.33333C2.66667 2.06971 2.06971 2.66667 1.33333 2.66667C0.596954 2.66667 0 2.06971 0 1.33333C0 0.596954 0.596954 0 1.33333 0C2.06971 0 2.66667 0.596954 2.66667 1.33333Z" />
      <path d="M7.33333 1.33333C7.33333 2.06971 6.73638 2.66667 6 2.66667C5.26362 2.66667 4.66667 2.06971 4.66667 1.33333C4.66667 0.596954 5.26362 0 6 0C6.73638 0 7.33333 0.596954 7.33333 1.33333Z" />
      <path d="M12 1.33333C12 2.06971 11.403 2.66667 10.6667 2.66667C9.93029 2.66667 9.33333 2.06971 9.33333 1.33333C9.33333 0.596954 9.93029 0 10.6667 0C11.403 0 12 0.596954 12 1.33333Z" />
    </g>
  </svg>
);

/** New conversation (compose / pencil-square). Not a Figma node: the conversation
 * header has no design yet, so this is the standard "new chat" compose affordance
 * (replaces the old menu-dots), tinting via `currentColor`. */
export const NewChatIcon = ({ size = 16, className }: GlyphProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <g stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13.5V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4.5" />
      <path d="M16.5 4.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 7.5-7.5Z" />
    </g>
  </svg>
);

/** Bold / Nature, Travel / Fire (node 4662:361). */
export const FireIcon = ({ size = 16, className }: GlyphProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      transform="translate(2 1.70242)"
      fill="currentColor"
      d="M6.55492 12.8317C8.63888 12.414 11.3333 10.9149 11.3333 7.03831C11.3333 3.51059 8.75112 1.16148 6.89435 0.0820895C6.48234 -0.157424 6 0.157566 6 0.634137V1.85312C6 2.81436 5.59585 4.56893 4.47288 5.29873C3.89955 5.67132 3.28035 5.11365 3.21067 4.43344L3.15345 3.87489C3.08693 3.22555 2.42561 2.83136 1.90664 3.22726C0.974311 3.93848 0 5.18392 0 7.03831C0 11.7791 3.52593 12.9642 5.28889 12.9642C5.39143 12.9642 5.49919 12.9612 5.61138 12.9547C4.74087 12.8803 3.33333 12.3402 3.33333 10.5937C3.33333 9.22747 4.33005 8.30319 5.08735 7.85392C5.29102 7.73309 5.52941 7.88998 5.52941 8.1268V8.51961C5.52941 8.82022 5.64567 9.29011 5.92242 9.61179C6.23559 9.97579 6.69528 9.59448 6.73234 9.11574C6.74404 8.9647 6.89593 8.86846 7.02672 8.9449C7.45426 9.19477 8 9.72855 8 10.5937C8 11.959 7.24735 12.5871 6.55492 12.8317Z"
    />
  </svg>
);

/** Outline / School / Square Academic Cap 2 (node 4662:368). */
export const AcademicCapIcon = ({ size = 16, className }: GlyphProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <g transform="translate(1.1664 1.5008)" fill="currentColor">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.14303 0.380383C6.22484 -0.126795 7.44203 -0.126794 8.52384 0.380383L12.9846 2.47171C13.6862 2.8006 14.0001 3.51482 14.0001 4.16668C14.0001 4.81855 13.6862 5.53276 12.9846 5.86166L12 6.32327V9.58362C12 10.419 11.5812 11.2255 10.8187 11.6478C10.3175 11.9253 9.65895 12.2594 8.96589 12.5253C8.281 12.7881 7.52261 13 6.83334 13C6.14406 13 5.38567 12.7881 4.70078 12.5253C4.00772 12.2594 3.34914 11.9253 2.84799 11.6478C2.08551 11.2255 1.66667 10.419 1.66667 9.58362V6.16667C1.66667 6.13904 1.66891 6.11194 1.67322 6.08554C1.65753 5.9895 1.66965 5.88789 1.71408 5.79313C1.8313 5.5431 2.12901 5.43544 2.37904 5.55266L5.56758 7.04752C6.38044 7.42861 7.28655 7.42861 8.09941 7.04752L12.5602 4.95622C12.8289 4.83021 13.0001 4.52962 13.0001 4.16668C13.0001 3.80374 12.8289 3.50315 12.5602 3.37714L8.09935 1.28582C7.28649 0.904728 6.38038 0.904728 5.56752 1.28582L1.10678 3.37711C0.856748 3.49433 0.559035 3.38667 0.441816 3.13664C0.324597 2.88661 0.432261 2.5889 0.682289 2.47168L5.14303 0.380383ZM2.66667 6.79195V9.58362C2.66667 10.0923 2.91917 10.5441 3.33246 10.773C3.81022 11.0376 4.42452 11.3483 5.05896 11.5917C5.70156 11.8382 6.32527 12 6.83334 12C7.3414 12 7.96511 11.8382 8.60771 11.5917C9.24215 11.3483 9.85645 11.0376 10.3342 10.773C10.7475 10.5441 11 10.0923 11 9.58362V6.7921L8.5239 7.95295C7.44209 8.46013 6.2249 8.46013 5.14309 7.95295L2.66667 6.79195Z"
      />
      <path d="M3.29291 3.63637C3.40169 3.89019 3.28411 4.18413 3.03029 4.29291L2.31318 4.60024C1.91133 4.77246 1.64657 4.88667 1.452 4.99612C1.26801 5.09963 1.1883 5.17696 1.13763 5.25379C1.08697 5.33062 1.0473 5.43435 1.02464 5.64423C1.00068 5.86618 1 6.15453 1 6.59172V8.5C1 8.77614 0.776144 9 0.500002 9C0.223859 9 5.36784e-07 8.77614 5.36784e-07 8.5V6.56583C-1.24972e-05 6.1615 -2.35438e-05 5.81883 0.0304173 5.53689C0.0628091 5.23688 0.133243 4.96042 0.302793 4.70329C0.472343 4.44617 0.698699 4.27253 0.961693 4.12457C1.20885 3.98553 1.52379 3.85057 1.89543 3.69131L2.63638 3.37376C2.89019 3.26498 3.18413 3.38256 3.29291 3.63637Z" />
    </g>
  </svg>
);

/** Stop glyph — a rounded square; replaces the send arrow while the assistant
 * is generating (click aborts the in-flight reply). Not a Figma node: the
 * conversation states have no design yet, so this mirrors the send disc geometry. */
export const StopIcon = ({ size = 20, className }: GlyphProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect x="5.5" y="5.5" width="9" height="9" rx="2.25" fill="currentColor" />
  </svg>
);

/** Send (paper-plane) — Figma node 6090:22914 (`Home`/send glyph). Replaces the
 * old up-arrow on the composer's send disc, on BOTH mobile and desktop. Drawn in
 * the icon's native 26-grid (the vector sits inset ~2px) so it scales cleanly to
 * any `size`; `fill: currentColor` lets the disc tint it (black on desktop, white
 * in the mobile orb). */
export const SendIcon = ({ size = 20, className }: GlyphProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 26 26" fill="none" aria-hidden="true">
    <path
      fill="currentColor"
      d="M20.188 16.9773L22.0475 11.3988C23.6719 6.52558 24.4841 4.08896 23.1979 2.80275C21.9117 1.51654 19.4751 2.32875 14.6018 3.95317L9.02337 5.81265C5.09019 7.12371 3.12359 7.77924 2.56475 8.74053C2.03311 9.65502 2.03311 10.7845 2.56475 11.699C3.12359 12.6602 5.09019 13.3158 9.02337 14.6268C9.51081 14.7893 10.0592 14.6733 10.4242 14.3117L16.3886 8.4025C16.7242 8.07006 17.2657 8.07258 17.5981 8.40813C17.9306 8.74368 17.9281 9.2852 17.5925 9.61764L11.7244 15.4314C11.3221 15.83 11.1947 16.44 11.3738 16.9773C12.6849 20.9104 13.3404 22.8771 14.3017 23.4359C15.2162 23.9675 16.3456 23.9675 17.2601 23.4359C18.2214 22.8771 18.8769 20.9105 20.188 16.9773Z"
    />
  </svg>
);

/** Close (bold X) — the Lumio header dismiss glyph on mobile and desktop (Figma
 * `vuesax/bold/close`), rendered dark inside a white disc. */
export const CloseIcon = ({ size = 16, className }: GlyphProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M4 4L12 12M12 4L4 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/** Proposals tray (inbox) — the header entry to the proposals inbox; wears a count
 * badge like an unread-mail icon. Not a Figma node: the proactive briefing has no
 * design yet, so this is a standard tray glyph tinting via `currentColor`. */
export const TrayIcon = ({ size = 18, className }: GlyphProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <g stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 13h4l2 3h4l2-3h4" />
      <path d="M4 13l2-8h12l2 8v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
    </g>
  </svg>
);

/** Back (chevron-left) — returns from the proposals inbox to the briefing. */
export const BackIcon = ({ size = 18, className }: GlyphProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Chevron-right — the "open" affordance on the briefing's Needs-you digest card. */
export const ChevronRightIcon = ({ size = 18, className }: GlyphProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Decision (check-in-circle) — the Needs-you digest glyph: pending decisions. */
export const DecisionIcon = ({ size = 18, className }: GlyphProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M9 12l2 2 4-4" />
    </g>
  </svg>
);
